import { useEffect, useMemo, useRef, type FC } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  buildPartsLayoutSignature,
  isTableColumnsSizedSF,
  tableElementRefAtom,
  type IExtendableDto,
  type IRydeHeaderGroup,
  type IRydeRow,
  type RenderSubComponentModel,
} from "@ryde/shared";

import { useTableColumnsSizingStore } from "../generic/table/hooks/use-table-columns-sizing-store";
import { GridPaging } from "../paging";
import { TableRowEmpty } from "../row-templates";
import {
  TableBodyStyled,
  TableHeadStyled,
  TableTagStyled,
} from "./grid.styled";
import { GridTableHeadCell } from "./grid.table.head-cell";
import { GridRow, type IGridRowProps } from "./grid.table.row";

interface IGridTable {
  renderSubComponent?: RenderSubComponentModel<IExtendableDto>;
  GridRowRenderer?: FC<IGridRowProps<IExtendableDto>>;
  headerGroups: IRydeHeaderGroup<IExtendableDto>[];
  rows: IRydeRow<IExtendableDto>[];
  /** Page grids only: their widths are stored per entity, which a grid nested in a form must not share. */
  isColumnResizeEnabled?: boolean;
}

// maybe add memoization for rows
export const GridTable: FC<IGridTable> = ({
  headerGroups,
  renderSubComponent,
  GridRowRenderer,
  rows,
  isColumnResizeEnabled = false,
}) => {
  const { entity } = usePageContext();
  const [tableElement, setTableElement] = useRecoilState(tableElementRefAtom);
  const tableRef = useRef<HTMLTableElement>(null);
  const isColumnsSized = useRecoilValue(isTableColumnsSizedSF(entity));
  const isTableSized = isColumnResizeEnabled && isColumnsSized;
  const columnsSizingStore = useTableColumnsSizingStore();

  // Derived from the same header groups the header row renders, so a memoized row can never keep a
  // column order the headers no longer show.
  const columnsLayoutSignature = useMemo(
    () =>
      buildPartsLayoutSignature(
        headerGroups.at(-1)?.headers.map(header => header.column.id) ?? [],
      ),
    [headerGroups],
  );

  useEffect(() => {
    // We need only first table for count dimensions
    if (tableElement) {
      return;
    }

    setTableElement(tableRef.current);
  }, [setTableElement, tableElement]);

  return (
    <>
      <TableTagStyled
        className="main_table"
        ref={tableRef}
        $isSized={isTableSized}
      >
        <TableHeadStyled>
          {headerGroups.map(headerGroup => (
            <tr data-e2e="table-head-row" key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <GridTableHeadCell<IExtendableDto>
                    key={header.id}
                    header={header}
                    isTableSized={isTableSized}
                    columnsSizingStore={
                      isColumnResizeEnabled ? columnsSizingStore : undefined
                    }
                  />
                );
              })}
            </tr>
          ))}
        </TableHeadStyled>
        <TableBodyStyled>
          {rows.map(row => {
            return GridRowRenderer ? (
              <GridRowRenderer
                key={row.id}
                row={row}
                renderSubComponent={renderSubComponent}
                columnsLayoutSignature={columnsLayoutSignature}
              />
            ) : (
              <GridRow
                key={row.id}
                row={row}
                renderSubComponent={renderSubComponent}
                columnsLayoutSignature={columnsLayoutSignature}
              />
            );
          })}

          {!rows?.length && (
            <TableRowEmpty cells={headerGroups[0]?.headers?.length} />
          )}
        </TableBodyStyled>
      </TableTagStyled>
      <GridPaging />
    </>
  );
};
